import type { WorkspaceItem } from '@/hooks/useWorkspace';
import type { TreeItem } from '@/lib/tree/types';
import { useParams } from 'next/navigation';
import { useMemo } from 'react';
import useSidebarChatbots from './useSidebarChatbots';

interface ActiveDirectory {
  id: string;
  name: string;
  type: WorkspaceItem['type'] | 'LIBRARY';
  teamId?: string | null;
}

export const useActiveSidebarLibrary = () => {
  const { sidebarOptions } = useSidebarChatbots();
  const params = useParams();
  const id = params?.id as string;

  const activePaths = useMemo(() => {
    if (id === 'private')
      return [
        { id: 'private', name: 'Private library', type: 'LIBRARY' as const },
      ];
    if (id === 'team')
      return [
        {
          id: 'team',
          name: 'Team library',
          type: 'LIBRARY' as const,
          teamId: sidebarOptions?.teamId,
        },
      ];

    if (!sidebarOptions?.tree || !id) return [];

    // Function to find a path to a chatbot in the tree
    const findPath = (
      treeItems: TreeItem[],
      targetId: string,
      currentPath: ActiveDirectory[] = [],
    ): ActiveDirectory[] | null => {
      for (const item of treeItems) {
        // Create a new path including this item
        const newPath = [
          ...currentPath,
          {
            id: item.id as string,
            name: item.name,
            type: item.type,
            teamId: item.teamId,
          },
        ];

        // If this is the target item, return the path
        if (item.id === targetId) {
          return newPath;
        }

        // Check children if they exist
        if (item.children && item.children.length > 0) {
          const pathInChildren = findPath(item.children, targetId, newPath);
          if (pathInChildren) {
            return pathInChildren;
          }
        }
      }

      // Item not found in this branch
      return null;
    };

    // Search for the chatbot in the tree
    return findPath(sidebarOptions.tree as TreeItem[], id) || [];
  }, [id, sidebarOptions]);

  const topLevel = activePaths[0];

  const lastPath = activePaths[activePaths.length - 1];

  return { topLevel, currentPath: lastPath };
};
